/**
 * Local, deterministic agent backend — no network, no API key.
 *
 * Parses a natural-language message into proposed CRM actions by matching
 * a set of intent patterns and resolving entity names against the live
 * store snapshot in `AgentContext`. It is intentionally conservative:
 * when it can't confidently resolve an entity or stage it says so rather
 * than guessing, and every action it does produce still flows through the
 * confirm-each-write gate in the UI.
 *
 * The vocabulary is deliberately broad — each intent accepts a family of
 * synonyms ("move/advance/bump/promote", "open/file/raise a ticket",
 * "remind me/follow up/add a task") so freeform phrasing lands on the
 * right action instead of the unhelpful "I didn't spot an action" reply.
 *
 * This implements the `AgentBackend` interface, so a hosted-LLM backend
 * can be dropped in later without touching ChatView.
 */

import {
  PIPELINE_STAGES,
  accountRollupStage,
  type Account,
  type BrainSection,
  type Person,
  type PipelineStage,
  type TaskPriority,
} from '../db/types';
import type {
  AgentAction,
  AgentBackend,
  AgentContext,
  AgentReply,
} from './types';
import { scanSystem, type ScanFinding } from './systemScanner';
import { BRAIN_SECTIONS } from '../brain/companyBrain';
import { capped, plural, tally, usd } from './dataSummary';
import { quickSuggestionsFor } from './quickSuggestions';

/** Stage an account workflow lands on when a deal is closed/won. */
const DEAL_WON_STAGE: PipelineStage = 'Deal Signed';

/**
 * Conversational lead-ins that carry no intent. Stripped (repeatedly, so
 * "Argo, can you please add…" peels down to "add…") before matching, so
 * natural phrasing lands on the same intent as the terse form.
 */
const PREAMBLES: RegExp[] = [
  /^(?:hey\s+)?argo[,!.]?\s+/i,
  /^(?:ok(?:ay)?|alright|right|so|now|well|hey|hi|yo)[,!.]?\s+/i,
  /^(?:please|pls|kindly|just|maybe|quickly)\s+/i,
  /^(?:can|could|would|will)\s+you\s+(?:please\s+)?/i,
  /^(?:i\s+(?:want|wanna|need|wish|would\s+like|'?d\s+like)\s+(?:to|you\s+to)\s+)/i,
  /^(?:i\s+(?:should|gotta|have\s+to|must)\s+)/i,
  /^(?:let'?s|lets)\s+/i,
  /^(?:go\s+ahead\s+and\s+)/i,
  /^(?:help\s+me\s+(?:to\s+)?)/i,
  /^(?:we\s+(?:should|need\s+to|gotta)\s+)/i,
  /^(?:you\s+(?:should|can|could)\s+)/i,
];

/** Peel conversational preambles off the front of an instruction. */
function stripPreamble(s: string): string {
  let out = s.trim();
  let changed = true;
  while (changed) {
    changed = false;
    for (const re of PREAMBLES) {
      const next = out.replace(re, '');
      if (next !== out) {
        out = next.trim();
        changed = true;
      }
    }
  }
  return out || s.trim();
}

/** Pronoun / vague reference that should resolve to the last-named entity. */
const PRONOUN =
  /^(?:them|they|her|him|it|that|this|those|these|that\s+one|the\s+(?:deal|contact|account|company|prospect|customer|client|one))$/i;

const PROSPECT_STAGES = [
  'new',
  'qualified',
  'proposal',
  'closed-won',
  'closed-lost',
] as const;
type ProspectStage = (typeof PROSPECT_STAGES)[number];
const CUSTOMER_STATUSES = ['active', 'paused', 'churned'] as const;
type CustomerStatus = (typeof CUSTOMER_STATUSES)[number];

/** Normalize for loose comparison. */
const norm = (s: string) => s.trim().toLowerCase();

/** Resolve a free-text name to a person, preferring exact > prefix > includes. */
function findPerson(people: Person[], query: string): Person | undefined {
  const q = norm(query);
  if (!q) return undefined;
  return (
    people.find((p) => norm(p.name) === q) ||
    people.find((p) => norm(p.name).startsWith(q)) ||
    people.find((p) => norm(p.name).includes(q)) ||
    people.find((p) => p.company && norm(p.company).includes(q))
  );
}

function findAccount(accounts: Account[], query: string): Account | undefined {
  const q = norm(query);
  if (!q) return undefined;
  return (
    accounts.find((a) => norm(a.name) === q) ||
    accounts.find((a) => norm(a.name).startsWith(q)) ||
    accounts.find((a) => norm(a.name).includes(q))
  );
}

/** Resolve free text to a Company Brain section (id or label match). */
function findBrainSection(query: string): BrainSection | undefined {
  const q = norm(query);
  if (!q) return undefined;
  const hit =
    BRAIN_SECTIONS.find((s) => s.id === q) ||
    BRAIN_SECTIONS.find((s) => norm(s.label) === q) ||
    BRAIN_SECTIONS.find((s) => s.id.startsWith(q) || norm(s.label).startsWith(q));
  return hit?.id;
}

/** Slug a label/value into a stable Brain field key (so writes upsert). */
function slugField(s: string): string {
  const slug = norm(s)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return slug || 'note';
}

/** Resolve free text to a canonical account-workflow pipeline stage. */
function findStage(query: string): PipelineStage | undefined {
  const q = norm(query);
  if (!q) return undefined;
  return (
    (PIPELINE_STAGES as readonly string[]).find((s) => norm(s) === q) ||
    (PIPELINE_STAGES as readonly string[]).find((s) => norm(s).includes(q)) ||
    (PIPELINE_STAGES as readonly string[]).find((s) => q.includes(norm(s)))
  ) as PipelineStage | undefined;
}

/** Map free text → prospect pipeline stage, with common synonyms. */
function resolveProspectStage(text: string): ProspectStage | undefined {
  const q = norm(text);
  if (!q) return undefined;
  // Synonyms first so "won"/"close the deal" land correctly.
  if (/\b(closed[\s-]?won|won|win|signed|landed)\b/.test(q)) return 'closed-won';
  if (/\b(closed[\s-]?lost|lost|lose|dead|dropped)\b/.test(q)) return 'closed-lost';
  if (/\b(qualif)/.test(q)) return 'qualified';
  if (/\bproposal\b/.test(q)) return 'proposal';
  if (/\bnew\b|\blead\b/.test(q)) return 'new';
  return PROSPECT_STAGES.find((s) => norm(s) === q || q.includes(norm(s)));
}

/** Map free text → customer lifecycle status, with synonyms. */
function resolveCustomerStatus(text: string): CustomerStatus | undefined {
  const q = norm(text);
  if (!q) return undefined;
  if (/\b(active|live|onboarded)\b/.test(q)) return 'active';
  if (/\b(paused|on[\s-]?hold|hold)\b/.test(q)) return 'paused';
  if (/\b(churn|churned|cancel|cancelled|canceled|lost)\b/.test(q)) return 'churned';
  return CUSTOMER_STATUSES.find((s) => norm(s) === q || q.includes(norm(s)));
}

/** Pull a dollar amount out of text, e.g. "$48k", "$120,000", "worth 50000". */
function parseMoney(text: string): number | undefined {
  // Require a leading digit so a stray comma ("Jane Doe ,") isn't read as 0.
  const m = /\$?\s*(\d[\d,]*(?:\.\d+)?)\s*([kKmM])?/.exec(text);
  if (!m) return undefined;
  let n = parseFloat(m[1].replace(/,/g, ''));
  if (Number.isNaN(n)) return undefined;
  const suffix = m[2]?.toLowerCase();
  if (suffix === 'k') n *= 1_000;
  else if (suffix === 'm') n *= 1_000_000;
  return Math.round(n);
}

/** Detect a task priority from free text. Defaults to undefined (= medium). */
function parsePriority(text: string): TaskPriority | undefined {
  const q = norm(text);
  if (/\b(urgent|asap|critical|p0|p1)\b/.test(q)) return 'urgent';
  if (/\b(high|important|priority)\b/.test(q)) return 'high';
  if (/\b(low|whenever|someday|p3)\b/.test(q)) return 'low';
  return undefined;
}

const ISO_DATE = /\b(\d{4}-\d{2}-\d{2})\b/;
const WEEKDAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

/** Format a Date as a plain ISO date (YYYY-MM-DD), local time. */
function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/**
 * Parse a due date out of free text. Handles explicit ISO dates,
 * "today"/"tomorrow", "in N day|week|month(s)", "next week/month",
 * weekday names, and "MM/DD". Returns YYYY-MM-DD or undefined.
 */
function parseDueDate(text: string): string | undefined {
  const q = norm(text);

  const iso = ISO_DATE.exec(text);
  if (iso) return iso[1];

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (/\btoday\b/.test(q)) return isoDate(today);
  if (/\btomorrow\b/.test(q)) {
    const d = new Date(today);
    d.setDate(d.getDate() + 1);
    return isoDate(d);
  }

  const inN = /\bin\s+(\d+)\s+(day|week|month)s?\b/.exec(q);
  if (inN) {
    const n = parseInt(inN[1], 10);
    const d = new Date(today);
    if (inN[2] === 'day') d.setDate(d.getDate() + n);
    else if (inN[2] === 'week') d.setDate(d.getDate() + n * 7);
    else d.setMonth(d.getMonth() + n);
    return isoDate(d);
  }

  if (/\bnext\s+week\b/.test(q)) {
    const d = new Date(today);
    d.setDate(d.getDate() + 7);
    return isoDate(d);
  }
  if (/\bnext\s+month\b/.test(q)) {
    const d = new Date(today);
    d.setMonth(d.getMonth() + 1);
    return isoDate(d);
  }

  // Weekday name → the next upcoming occurrence (1–7 days out).
  const wd = WEEKDAYS.findIndex((w) => new RegExp(`\\b${w}\\b`).test(q));
  if (wd >= 0) {
    const d = new Date(today);
    const delta = ((wd - d.getDay() + 7) % 7) || 7;
    d.setDate(d.getDate() + delta);
    return isoDate(d);
  }

  // MM/DD (this year, or next year if already past).
  const md = /\b(\d{1,2})\/(\d{1,2})\b/.exec(q);
  if (md) {
    const month = parseInt(md[1], 10) - 1;
    const day = parseInt(md[2], 10);
    if (month >= 0 && month < 12 && day >= 1 && day <= 31) {
      const d = new Date(today.getFullYear(), month, day);
      if (d < today) d.setFullYear(d.getFullYear() + 1);
      return isoDate(d);
    }
  }

  return undefined;
}

const HELP = [
  'I can act on your CRM — every action waits for your confirmation before it writes. Try:',
  '• “Add prospect Jane Doe at Acme, deal worth $48k”',
  '• “Create a proposal for <account or prospect>”',
  '• “Draft a contract for <customer>”',
  '• “Move <account> to Deal Signed” · “Bump <prospect> to qualified”',
  '• “Mark <prospect> as won” · “Set <customer> to churned”',
  '• “Open a ticket to fix the billing export (p1)”',
  '• “Add a task to send the SOW to Acme by Friday (high)”',
  '• “Follow up with Jane Doe next week” — sets a CRM reminder',
  '• “Log a call with Jane Doe about pricing” — drops a note on her timeline',
  '• “Remember for Acme’s brain: renewal date = March” — writes a Company Brain fact',
  'You can chain several in one message: “Add prospect Jane at Acme and follow up Friday and open a ticket to send the deck”.',
].join('\n');

/**
 * Does this message read as a "what should I focus on" question rather than
 * a command? If so we answer it from the live scanner instead of trying to
 * parse an action out of it.
 */
function isWorkQuestion(text: string): boolean {
  const q = norm(text);
  return (
    /\bwhat\s+should\s+i\s+(?:do|focus|work\s+on|tackle|start|prioriti[sz]e)/.test(q) ||
    /\bwhat(?:'s| is| are)?\b[^?]*\b(?:on\s+my\s+plate|needs?\s+(?:my\s+)?attention|attention|overdue|urgent|pressing|on\s+fire|priorit|pending|due\s+today|do\s+today|next)\b/.test(q) ||
    /\b(?:anything|something)\s+(?:overdue|urgent|pressing|on\s+fire|i\s+should\s+(?:do|know))\b/.test(q) ||
    /\b(?:my\s+(?:priorities|follow[\s-]?ups|tasks\s+today)|status\s+(?:report|update|check)|daily\s+summary|summari[sz]e\s+(?:my\s+)?(?:day|crm|system|pipeline)|what'?s\s+going\s+on|where\s+(?:do|should)\s+i\s+start)\b/.test(q) ||
    /\b(?:needs?|need)\s+(?:my\s+)?attention\b/.test(q)
  );
}

/** Plain ISO date for tomorrow — the "tight deadline" chase actions carry. */
function tomorrowIso(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  return isoDate(d);
}

/**
 * Turn the scanner's top findings into concrete, confirmable proposals so a
 * "what needs attention" answer arrives with one-click action chips — never
 * a prose offer ("Want me to…?") the operator has to answer by typing.
 *
 * Every action is grounded in a real record from the live context (nothing
 * invented), uses only existing AgentAction kinds, and still routes through
 * the confirm-each-write gate before anything is written.
 */
function actionsForFindings(
  findings: ScanFinding[],
  ctx: AgentContext,
): AgentAction[] {
  // Cap the batch at ~6 so the "Handle all" chip stays a single reasonable
  // decision. Findings arrive severity-ranked (high → medium → low), so the
  // proposals we keep are the most pressing ones — same order the digest shows.
  const MAX_ACTIONS = 6;
  const actions: AgentAction[] = [];
  const due = tomorrowIso();
  const push = (a: AgentAction) => {
    if (actions.length >= MAX_ACTIONS) return;
    if (!actions.some((x) => JSON.stringify(x) === JSON.stringify(a))) actions.push(a);
  };

  for (const f of findings) {
    if (actions.length >= MAX_ACTIONS) break;

    if (f.source === 'task') {
      const task = (ctx.tasks ?? []).find((t) => t.id === f.entityId);
      if (!task) continue;
      // Tight-deadline follow-up task, carrying the original's entity links.
      push({
        kind: 'create_task',
        title: `Follow up: ${task.title}`,
        priority: task.priority === 'urgent' ? 'urgent' : 'high',
        dueDate: due,
        assignee: task.assignee ?? undefined,
        personId: task.personId,
        personName: task.personName,
        accountId: task.accountId,
        accountName: task.accountName,
      });
      // Note on the person the task concerns (or the account's matching contact).
      const person =
        (task.personId
          ? ctx.people.find((p) => p.id === task.personId)
          : undefined) ??
        (task.accountName
          ? ctx.people.find(
              (p) => p.company && norm(p.company) === norm(task.accountName!),
            )
          : undefined);
      if (person) {
        push({
          kind: 'log_activity',
          personId: person.id,
          personName: person.name,
          activityKind: 'note',
          text: `${f.category} “${task.title}” — ${f.detail}`,
        });
      }
    } else if (f.source === 'prospect') {
      const p = ctx.people.find((x) => x.id === f.entityId);
      if (!p) continue;
      push({
        kind: 'schedule_follow_up',
        personId: p.id,
        personName: p.name,
        followUpDate: due,
        note: `Re-engage — ${f.category.toLowerCase()}`,
      });
      push({
        kind: 'log_activity',
        personId: p.id,
        personName: p.name,
        activityKind: 'note',
        text: `${f.category}: ${f.detail}`,
      });
    } else if (f.source === 'ticket') {
      const t = ctx.tickets.find((x) => x.id === f.entityId);
      if (!t) continue;
      // An unowned ticket with a real staff candidate → propose the assign.
      if (!t.owner) {
        const staff = ctx.people.find((p) => p.category === 'staff');
        if (staff) {
          push({
            kind: 'assign_ticket',
            ticketId: t.id,
            ticketTitle: t.title,
            owner: staff.name,
            fromOwner: null,
          });
        }
      }
      push({
        kind: 'create_task',
        title: `Chase ticket ${t.id}: ${t.title}`,
        priority: t.priority === 'p0' || t.priority === 'p1' ? 'urgent' : 'high',
        dueDate: due,
      });
    } else if (f.source === 'account') {
      // Billing/renewal findings carry the customer's person id.
      const p = ctx.people.find((x) => x.id === f.entityId);
      if (!p) continue;
      push({
        kind: 'create_task',
        title: `${f.category}: ${p.name}`,
        priority: 'high',
        dueDate: due,
        personId: p.id,
        personName: p.name,
        value: p.accountValue,
      });
      push({
        kind: 'log_activity',
        personId: p.id,
        personName: p.name,
        activityKind: 'note',
        text: `${f.category}: ${f.detail}`,
      });
    }
  }

  return actions;
}

/**
 * Build a prioritized "what to look at" answer from the same scanner the
 * Dashboard uses — plus concrete one-click proposals for the top findings,
 * so the reply can act (via confirm chips), not just narrate. Nothing runs
 * until the operator confirms a card.
 */
function answerWorkQuestion(ctx: AgentContext): AgentReply {
  const { findings, summary } = scanSystem({
    tickets: ctx.tickets,
    tasks: ctx.tasks ?? [],
    people: ctx.people,
    accounts: ctx.accounts,
  });
  if (summary.total === 0) {
    return {
      text: 'Nothing is overdue, blocked, unowned, or stale right now — your tickets, tasks, prospects, and accounts are all current. You’re clear. 🎉',
      actions: [],
    };
  }
  // A tight ranked list — one line per item. The detail lives in the chips
  // below, so the text stays scannable and the actions carry the weight.
  const top = findings.slice(0, 6);
  const lines = top.map(
    (f, i) => `${i + 1}. [${f.severity.toUpperCase()}] ${f.title} — ${f.detail}`,
  );
  const more =
    summary.total > top.length ? `\n…and ${summary.total - top.length} more.` : '';
  const header = `Here’s what needs attention, ranked (${summary.high} high · ${summary.medium} medium · ${summary.low} low):`;
  const actions = actionsForFindings(findings, ctx);
  // Declarative closing — never an offer-question. When we lined up chips,
  // point at them; otherwise state the batch chip / manual path plainly.
  const hint = actions.length
    ? `\n\nActions below — approve what you want done${actions.length > 2 ? ', or “Handle all” to queue them together' : ''}.`
    : '\n\nNo one-click fix fits these — handle them directly on their records.';
  return { text: `${header}\n\n${lines.join('\n')}${more}${hint}`, actions };
}

/**
 * When a data question resolved to a specific known entity ("tell me about
 * Jane"), attach the same one-click chips the composer suggests for it —
 * so the answer carries buttons, not a prose offer to help.
 */
function entityFollowUpActions(raw: string, ctx: AgentContext): AgentAction[] {
  const entity = extractEntityQuery(stripPreamble(raw));
  if (!entity || entity.length < 2) return [];
  const acct = findAccount(ctx.accounts, entity);
  const person = findPerson(ctx.people, entity);
  // Mirror answerDataQuestion's precedence: exact account name wins, else person.
  const recent =
    acct && (!person || norm(acct.name) === norm(entity))
      ? { accountId: acct.id, accountName: acct.name }
      : person
        ? { personId: person.id, personName: person.name }
        : acct
          ? { accountId: acct.id, accountName: acct.name }
          : undefined;
  return quickSuggestionsFor(recent, ctx).map((s) => s.action);
}

interface ClauseResult {
  actions: AgentAction[];
  notes: string[];
}

/**
 * Map a free-text activity word to a canonical timeline kind.
 */
function resolveActivityKind(word: string): 'note' | 'call' | 'email' | 'meeting' {
  const q = norm(word);
  if (/(call|voicemail|phone|dial)/.test(q)) return 'call';
  if (/(email|mail|message)/.test(q)) return 'email';
  if (/(meeting|met|conversation|chat|sync|demo|sit[\s-]?down)/.test(q)) return 'meeting';
  return 'note';
}

/**
 * Parse a single action clause into proposed actions + notes. `interpret`
 * splits a multi-part message into clauses and merges the results, so this
 * only has to reason about one instruction at a time.
 */
function parseClause(text: string, ctx: AgentContext): ClauseResult {
  {
    text = stripPreamble(text);
    const actions: AgentAction[] = [];
    const notes: string[] = [];

    // ---- Add customer / prospect / lead -------------------------------
    // "add prospect <name>", "add a customer named <name>", "new lead <name>",
    // and the natural inversions "create Jane Smith as a prospect" /
    // "add Acme Corp as a customer".
    let addCategory: 'customer' | 'prospect' | undefined;
    let addRest: string | undefined;
    const addMatch =
      /\b(?:add|create|new|register|onboard)\s+(?:a\s+|an\s+)?(customer|prospect|lead|contact)\b(?:\s+(?:named|called)\s+|\s+)(.+)/i.exec(
        text,
      );
    // Ignore an "add" hit whose name starts with a linking verb — that's the
    // "<name> is a customer" form (handled below), not "new contact <name>".
    if (addMatch && !/^\s*(?:is|are|was|were|’s|'s)\b/i.test(addMatch[2])) {
      const raw = norm(addMatch[1]);
      addCategory = raw === 'customer' ? 'customer' : 'prospect';
      addRest = addMatch[2];
    } else {
      // "<verb> <name> as a <category>" — category trails the name.
      const addAs =
        /\b(?:add|create|new|register|onboard|set\s+up)\s+(.+?)\s+as\s+(?:a\s+|an\s+|my\s+|our\s+)?(customer|prospect|lead|contact|client)\b/i.exec(
          text,
        );
      if (addAs) {
        const raw = norm(addAs[2]);
        addCategory = raw === 'customer' || raw === 'client' ? 'customer' : 'prospect';
        addRest = addAs[1];
      }
    }
    if (addCategory && addRest != null) {
      // "lead"/"contact" both default to the prospect pipeline.
      const category: 'customer' | 'prospect' = addCategory;
      let rest = addRest.trim();
      // Pull "at <company>"
      let company: string | undefined;
      const atMatch = /\bat\s+([^,.;]+)/i.exec(rest);
      if (atMatch) {
        company = atMatch[1].trim();
        rest = rest.replace(atMatch[0], '').trim();
      }
      // Pull email
      let email: string | undefined;
      const emailMatch = /([^\s,;]+@[^\s,;]+\.[^\s,;]+)/.exec(rest);
      if (emailMatch) {
        email = emailMatch[1];
        rest = rest.replace(emailMatch[0], '').trim();
      }
      // Pull money (deal / value)
      const value =
        /\$|worth|deal|value/i.test(rest) ? parseMoney(rest) : undefined;
      // Name = leading words before a comma / "worth" / "$"
      const name = rest
        .split(/[,;]| worth | deal | email /i)[0]
        .replace(/\$\s*[\d,.]+\s*[kmKM]?/g, '')
        .trim();
      if (name) {
        actions.push({
          kind: 'create_person',
          category,
          name,
          email,
          company,
          value,
        });
      } else {
        notes.push('I couldn’t pick out a name to add — try “Add prospect Jane Doe”.');
      }
    }

    // ---- Implicit status: "<name> is (now) a customer/prospect/…" ------
    // Converts an existing contact (prospect won → customer; status change)
    // or, for a brand-new name, proposes the create. Conservative: the
    // create branch only fires for a capitalized proper-noun subject so a
    // line like "the deal is signed" doesn't invent a contact.
    const isMatch =
      /^(.+?)\s+is\s+(?:now\s+)?(?:a\s+|an\s+)?(customer|client|prospect|lead|churned|cancelled|canceled|active|live|paused|on[\s-]?hold|qualified|won|lost|signed|dead)\b/i.exec(
        text,
      );
    if (isMatch && !addCategory) {
      const who = isMatch[1].trim();
      const stateText = norm(isMatch[2]);
      const wantsCustomer = /^(?:customer|client)$/.test(stateText);
      const custStatus = resolveCustomerStatus(stateText);
      const prospStage = resolveProspectStage(stateText);
      const person = findPerson(ctx.people, who);
      if (person) {
        if (person.category === 'prospect') {
          // "is now a customer" on a prospect = the deal closed-won.
          const stage = wantsCustomer ? 'closed-won' : prospStage;
          if (stage) {
            actions.push({
              kind: 'set_prospect_stage',
              personId: person.id,
              personName: person.name,
              stage,
            });
          }
        } else if (custStatus) {
          actions.push({
            kind: 'set_customer_status',
            personId: person.id,
            personName: person.name,
            status: custStatus,
          });
        }
      } else if (/^[A-Z]/.test(who) && !PRONOUN.test(who) && (wantsCustomer || /^(?:prospect|lead)$/.test(stateText))) {
        actions.push({
          kind: 'create_person',
          category: wantsCustomer ? 'customer' : 'prospect',
          name: who,
        });
      }
    }

    // ---- Create proposal / contract -----------------------------------
    const docMatch =
      /\b(?:create|draft|generate|make|send|prepare|put\s+together|write\s+up|build)\s+(?:a\s+|an\s+)?(proposal|contract|agreement|sow)\b\s*(?:for|to|with)?\s*(.+)?/i.exec(
        text,
      );
    if (docMatch && docMatch[2]) {
      const rawKind = norm(docMatch[1]);
      const docKind: 'proposal' | 'contract' =
        rawKind === 'proposal' ? 'proposal' : 'contract';
      let target = docMatch[2].trim();
      const value = parseMoney(target);
      target = target.replace(/\bfor\b|\$\s*[\d,.]+\s*[kmKM]?|worth/gi, '').trim();
      const targetName = target.split(/[,;.]/)[0].trim();

      // "draft a proposal for her/that company" → reuse the last-named entity.
      const usePronoun = PRONOUN.test(targetName) && ctx.recent;
      const acct = usePronoun
        ? ctx.recent!.accountId
          ? ctx.accounts.find((a) => a.id === ctx.recent!.accountId)
          : undefined
        : findAccount(ctx.accounts, targetName);
      const person = usePronoun
        ? ctx.recent!.personId
          ? ctx.people.find((p) => p.id === ctx.recent!.personId)
          : undefined
        : findPerson(ctx.people, targetName);
      // Contracts conventionally attach to customer accounts; proposals to
      // prospects. Prefer the more specific match but fall back gracefully.
      if (acct && (docKind === 'contract' || !person)) {
        actions.push({
          kind: 'create_document',
          docKind,
          entityType: 'account',
          entityId: acct.id,
          entityName: acct.name,
          contactName: acct.pocName,
          ownerName: acct.ownerName,
          value,
        });
      } else if (person) {
        actions.push({
          kind: 'create_document',
          docKind,
          entityType: 'person',
          entityId: person.id,
          entityName: person.company || person.name,
          contactName: person.name,
          ownerName: person.ownerName,
          value: value ?? person.estimatedValue ?? person.accountValue,
        });
      } else {
        notes.push(
          `I couldn’t find an account or contact matching “${targetName}” to attach the ${docKind} to.`,
        );
      }
    }

    // ---- Add a task / follow-up ---------------------------------------
    // "add a task to <x>", "todo: <x>", "remind me to <x>", "follow up with <who>"
    const taskMatch =
      /\b(?:add|create|make|log|new)\s+(?:a\s+)?(?:task|to-?do|reminder)\b(?:\s*(?:to|for|:)\s*)?(.*)/i.exec(
        text,
      ) ||
      /\b(?:remind\s+me\s+to|to-?do:?|task:)\s*(.*)/i.exec(text) ||
      /\b(follow[\s-]?up)\b\s*(.*)/i.exec(text);
    if (taskMatch && !docMatch) {
      const isFollowUp = /follow[\s-]?up/i.test(taskMatch[0]);
      let rest = (isFollowUp ? taskMatch[2] : taskMatch[1] ?? '').trim();

      // Tie to a contact/account via "for <who>" / "with <who>" / "to <who>".
      let personId: string | undefined;
      let personName: string | undefined;
      let accountId: string | undefined;
      let accountName: string | undefined;
      const tieMatch = /\b(?:for|with|to)\s+([A-Z][^,.;]*?)(?=\s+(?:by|on|next|tomorrow|today|in|about|re:|—|-)\b|[,.;]|$)/i.exec(
        rest,
      );
      if (tieMatch) {
        const who = tieMatch[1].trim();
        const q = norm(who);
        // Precedence: an exact account-name match wins (so "to Acme" ties to
        // the org, not the contact-at-Acme), then any person, then a loose
        // account. Keeps "with Jane Doe" → the person.
        const exactAcct = ctx.accounts.find((a) => norm(a.name) === q);
        const person = !exactAcct ? findPerson(ctx.people, who) : undefined;
        const acct = exactAcct ?? (!person ? findAccount(ctx.accounts, who) : undefined);
        if (acct) {
          accountId = acct.id;
          accountName = acct.name;
          rest = rest.replace(tieMatch[0], ' ').trim();
        } else if (person) {
          personId = person.id;
          personName = person.name;
          rest = rest.replace(tieMatch[0], ' ').trim();
        }
      }

      // Pull a due date + priority, then strip the phrases that produced them.
      const dueDate = parseDueDate(rest);
      const priority = parsePriority(rest);
      let title = rest
        .replace(/\bby\s+/gi, ' ')
        .replace(/\bon\s+/gi, ' ')
        .replace(ISO_DATE, ' ')
        .replace(/\b(today|tomorrow|next\s+week|next\s+month)\b/gi, ' ')
        .replace(/\bin\s+\d+\s+(?:day|week|month)s?\b/gi, ' ')
        .replace(new RegExp(`\\b(${WEEKDAYS.join('|')})\\b`, 'gi'), ' ')
        .replace(/\b\d{1,2}\/\d{1,2}\b/g, ' ')
        .replace(/\(?\s*\b(urgent|asap|critical|high|important|priority|low|p[0-3])\b\s*\)?/gi, ' ')
        .replace(/\(\s*\)/g, '')
        .replace(/\s{2,}/g, ' ')
        .replace(/^[\s:—-]+|[\s,.;]+$/g, '')
        .trim();

      // A follow-up tied to a *contact* becomes a CRM reminder
      // (schedule_follow_up sets followUpDate + nextStep) rather than a
      // generic task, so it feeds the follow-up reminders surface.
      if (isFollowUp && personId && personName) {
        const note =
          title && !/^follow[\s-]?up/i.test(title) ? title : undefined;
        actions.push({
          kind: 'schedule_follow_up',
          personId,
          personName,
          followUpDate: dueDate,
          note,
        });
      } else {
        // For a bare "follow up with <who>", synthesize a sensible title.
        if (!title && isFollowUp && accountName) title = `Follow up with ${accountName}`;
        else if (isFollowUp && title && !/follow/i.test(title))
          title = `Follow up: ${title}`;

        if (title) {
          actions.push({
            kind: 'create_task',
            title,
            priority,
            dueDate,
            personId,
            personName,
            accountId,
            accountName,
          });
        } else {
          notes.push(
            'What should the task be? e.g. “Add a task to send the SOW to Acme by Friday”.',
          );
        }
      }
    }

    // ---- Log an activity (call / note / email / meeting) on a contact -
    // "log a call with Jane about pricing", "record a meeting with Acme"
    const logMatch =
      /\b(?:log|record)\s+(?:a\s+|an\s+)?(call|voicemail|phone|email|mail|message|meeting|conversation|chat|sync|demo|note)\b(.*)/i.exec(
        text,
      );
    if (logMatch && !taskMatch && !docMatch) {
      const activityKind = resolveActivityKind(logMatch[1]);
      let rest = (logMatch[2] ?? '').trim();
      // Resolve the contact via "with <who>".
      let person: Person | undefined;
      const withMatch =
        /\bwith\s+([A-Z][^,.;]*?)(?=\s+(?:about|re:?|regarding|saying|that|on|today|yesterday)\b|[,.;]|$)/i.exec(
          rest,
        );
      if (withMatch) {
        person = findPerson(ctx.people, withMatch[1].trim());
        if (person) rest = rest.replace(withMatch[0], ' ').trim();
      }
      // The body is whatever follows about/re/regarding/saying/that.
      let body = rest
        .replace(/^\s*(?:about|re:?|regarding|saying|that|on|—|-|:)\s*/i, '')
        .replace(/\s{2,}/g, ' ')
        .trim();
      if (!body) body = `${activityKind[0].toUpperCase()}${activityKind.slice(1)} logged`;
      if (person) {
        actions.push({
          kind: 'log_activity',
          personId: person.id,
          personName: person.name,
          activityKind,
          text: body,
        });
      } else if (withMatch) {
        notes.push(`I couldn’t find a contact named “${withMatch[1].trim()}” to log that against.`);
      } else {
        notes.push('Who was that with? e.g. “Log a call with Jane Doe about pricing”.');
      }
    }

    // ---- Move / advance an account workflow to a stage ----------------
    const moveMatch =
      /\b(?:move|advance|push|set|progress|bump|promote)\s+(.+?)\s+(?:to|into|->|→)\s+(.+)/i.exec(
        text,
      );
    if (moveMatch && !docMatch && !taskMatch) {
      const who = moveMatch[1].trim();
      const stageText = moveMatch[2].trim();
      const stage = findStage(stageText);
      const acct = findAccount(ctx.accounts, who);
      const prospectStage = resolveProspectStage(stageText);
      const customerStatus = resolveCustomerStatus(stageText);

      if (acct && stage) {
        // Pick a workflow: if one, use it; else try name match in `who`,
        // else default to the first workflow.
        const wf =
          acct.workflows.length === 1
            ? acct.workflows[0]
            : acct.workflows.find((w) => norm(who).includes(norm(w.name))) ||
              acct.workflows[0];
        if (wf) {
          actions.push({
            kind: 'move_workflow_stage',
            accountId: acct.id,
            accountName: acct.name,
            workflowId: wf.id,
            workflowName: wf.name,
            fromStage: wf.stage,
            toStage: stage,
          });
        } else {
          notes.push(`${acct.name} has no workflows to advance yet.`);
        }
      } else {
        // Maybe it's a person stage/status change.
        const person = findPerson(ctx.people, who);
        if (person && person.category === 'prospect' && prospectStage) {
          actions.push({
            kind: 'set_prospect_stage',
            personId: person.id,
            personName: person.name,
            stage: prospectStage,
          });
        } else if (person && person.category === 'customer' && customerStatus) {
          actions.push({
            kind: 'set_customer_status',
            personId: person.id,
            personName: person.name,
            status: customerStatus,
          });
        } else if (!acct && !person) {
          notes.push(`I couldn’t find “${who}” among accounts or contacts.`);
        } else if (acct && !stage) {
          notes.push(`“${stageText}” isn’t a pipeline stage I recognize.`);
        }
      }
    }

    // ---- Mark prospect/customer as <stage/status> ---------------------
    const markMatch =
      /\bmark\s+(.+?)\s+as\s+(.+)/i.exec(text) ||
      /\bset\s+(.+?)\s+(?:stage|status)\s+(?:to\s+)?(.+)/i.exec(text) ||
      /\b(?:close|win|land)\s+(?:the\s+)?(?:deal\s+(?:with|for)\s+)?(.+)/i.exec(text);
    if (markMatch) {
      const isClose = /^(?:close|win|land)/i.test(markMatch[0]);
      const who = (isClose ? markMatch[1] : markMatch[1]).trim();
      const stateText = isClose ? 'closed-won' : norm(markMatch[2].trim());
      const person = findPerson(ctx.people, who);
      const prospectStage = resolveProspectStage(stateText);
      const customerStatus = resolveCustomerStatus(stateText);
      if (person && prospectStage && person.category !== 'customer') {
        if (!actions.some((a) => a.kind === 'set_prospect_stage' && a.personId === person.id)) {
          actions.push({
            kind: 'set_prospect_stage',
            personId: person.id,
            personName: person.name,
            stage: prospectStage,
          });
        }
      } else if (person && customerStatus) {
        if (!actions.some((a) => a.kind === 'set_customer_status' && a.personId === person.id)) {
          actions.push({
            kind: 'set_customer_status',
            personId: person.id,
            personName: person.name,
            status: customerStatus,
          });
        }
      } else if (isClose) {
        // "close the deal with <account>" → advance that account's workflow
        // to Deal Signed (the closing stage). Falls back to the contact path
        // above when <who> is a person.
        const acct = findAccount(ctx.accounts, who);
        if (acct) {
          const wf =
            acct.workflows.find((w) => norm(who).includes(norm(w.name))) ||
            acct.workflows[0];
          if (!wf) {
            notes.push(`${acct.name} has no workflow to close yet.`);
          } else if (wf.stage === DEAL_WON_STAGE) {
            notes.push(`${acct.name}’s “${wf.name}” is already at ${DEAL_WON_STAGE}.`);
          } else if (
            !actions.some(
              (a) => a.kind === 'move_workflow_stage' && a.workflowId === wf.id,
            )
          ) {
            actions.push({
              kind: 'move_workflow_stage',
              accountId: acct.id,
              accountName: acct.name,
              workflowId: wf.id,
              workflowName: wf.name,
              fromStage: wf.stage,
              toStage: DEAL_WON_STAGE,
            });
          }
        } else if (!person) {
          notes.push(`I couldn’t find “${who}” among contacts or accounts to close.`);
        }
      } else if (!person) {
        notes.push(`I couldn’t find a contact named “${who}”.`);
      }
    }

    // ---- Write a fact into an account's Company Brain ------------------
    // "remember for <account>'s brain: <label> = <value>", optionally with
    // a "(section)" or "section:" hint. Requires the literal word "brain"
    // so it never shadows a timeline note (log_activity) or a contact add.
    // The confirm card IS the operator confirming the fact, so it lands as
    // an `operator`-sourced Brain write; the no-silent-downgrade guard in
    // putBrainFact still protects any higher-confidence founder/research
    // value. This is the P1 writable-Company-Brain (`update_brain`) seam.
    const brainMatch =
      /\b(?:remember|note|record|capture|save|jot)\b\s+(?:that\s+)?(?:for|in|on|to|about\s+)?\s*(.+?)(?:’s|'s)?\s+brain\b\s*[:,–—-]?\s*(.+)/i.exec(
        text,
      );
    if (brainMatch) {
      const acctQuery = brainMatch[1].trim();
      const acct = findAccount(ctx.accounts, acctQuery);
      if (!acct) {
        notes.push(
          `I couldn’t find an account named “${acctQuery}” to write to its Brain.`,
        );
      } else {
        let body = brainMatch[2].trim();
        // Optional leading "(section)" or "section:" hint — only consumed
        // when the word actually resolves to a Brain section, so a real
        // "label: value" pair isn't mistaken for a section.
        let section: BrainSection = 'history';
        const parenSec = /^\(([^)]+)\)\s*/.exec(body);
        const headSec = /^([a-z]+)\s*[:\-]\s*/i.exec(body);
        const secWord = parenSec?.[1] ?? headSec?.[1];
        const resolved = secWord ? findBrainSection(secWord) : undefined;
        if (resolved) {
          section = resolved;
          body = body.replace(parenSec ? parenSec[0] : headSec![0], '').trim();
        }
        // "label = value" / "label: value" → split; else the whole body is
        // the value under a generic "Note" label.
        let label = 'Note';
        let value = body;
        const kv = /^(.{1,60}?)\s*[:=]\s*(.+)$/.exec(body);
        if (kv) {
          label = kv[1].trim();
          value = kv[2].trim();
        }
        if (value) {
          actions.push({
            kind: 'update_brain',
            accountId: acct.id,
            accountName: acct.name,
            section,
            field: slugField(label === 'Note' ? value : label),
            label,
            value,
          });
        } else {
          notes.push(
            'What should I remember? Try “remember for Acme’s brain: renewal date = March”.',
          );
        }
      }
    }

    // ---- Open a ticket -------------------------------------------------
    const ticketMatch =
      /\b(?:open|create|file|new|raise|log)\s+(?:a\s+)?ticket\b(?:\s+(?:to|for|:)\s*)?(.*)/i.exec(
        text,
      );
    if (ticketMatch) {
      let title = ticketMatch[1].trim();
      // Pull a priority like "(p1)" or "p0", consuming any wrapping parens.
      let priority: 'p0' | 'p1' | 'p2' | 'p3' | undefined;
      const prioMatch = /\(?\s*\b(p[0-3])\b\s*\)?/i.exec(title);
      if (prioMatch) {
        priority = norm(prioMatch[1]) as 'p0' | 'p1' | 'p2' | 'p3';
        title = title.replace(prioMatch[0], '');
      }
      // Tidy leftovers: empty parens, doubled spaces, trailing punctuation.
      title = title.replace(/\(\s*\)/g, '').replace(/\s{2,}/g, ' ').replace(/[,.;\s]+$/, '').trim();
      if (title) {
        actions.push({ kind: 'create_ticket', title, priority });
      } else {
        notes.push('What should the ticket be about? e.g. “Open a ticket to fix the export”.');
      }
    }

    return { actions, notes };
  }
}

/**
 * Split a message into independent action clauses. We only break on a
 * connector ("; ", " then ", " and ") when the next word is a known action
 * verb, so "Jane Doe and Acme" inside a single instruction stays intact
 * while "add Jane and open a ticket" splits into two actions.
 */
function splitClauses(text: string): string[] {
  const parts = text
    .split(
      /\s*(?:;|\bthen\b|,?\s+\band\b)\s+(?=(?:add|create|new|register|onboard|draft|generate|make|prepare|build|put|write|send|open|file|raise|log|record|note|remember|capture|save|jot|move|advance|push|set|progress|bump|promote|mark|close|win|land|remind|follow|to-?do|task)\b)/i,
    )
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length > 1 ? parts : [text];
}

// ===========================================================================
// Read-only question answering — lets the chat answer "how many prospects do
// we have?", "what's my biggest deal?", "list my customers", "tell me about
// Acme" straight from the live store. This runs ONLY in interpret()'s
// no-action branch (after every command parser has had its turn), so it can
// never hijack a real write. It proposes nothing — pure narration.
// ===========================================================================

// The usd/plural/tally/capped formatting helpers used below were lifted
// into './dataSummary' (shared with the agent-context builder) — same
// behaviour, one implementation.

/** One-line résumé of a contact for "tell me about <name>". */
function personSummary(p: Person): string {
  const bits: string[] = [];
  bits.push(`${p.name}${p.title ? `, ${p.title}` : ''}${p.company ? ` @ ${p.company}` : ''}`);
  if (p.category === 'prospect') {
    const meta = [
      p.stage ? `stage ${p.stage}` : null,
      p.estimatedValue ? `deal ${usd(p.estimatedValue)}` : null,
      typeof p.score === 'number' ? `score ${p.score}` : null,
    ].filter(Boolean);
    if (meta.length) bits.push(`Prospect — ${meta.join(', ')}.`);
    else bits.push('Prospect.');
  } else if (p.category === 'customer') {
    const meta = [
      p.status ? `status ${p.status}` : null,
      p.accountValue ? `${usd(p.accountValue)}/yr` : null,
      p.renewalDate ? `renews ${p.renewalDate}` : null,
    ].filter(Boolean);
    if (meta.length) bits.push(`Customer — ${meta.join(', ')}.`);
    else bits.push('Customer.');
  } else if (p.category === 'staff') {
    bits.push('Team member.');
  }
  if (p.nextStep) bits.push(`Next step: ${p.nextStep}.`);
  if (p.followUpDate) bits.push(`Follow up by ${p.followUpDate}.`);
  if (p.email) bits.push(p.email);
  return bits.join(' ');
}

/** One-line résumé of an account for "tell me about <account>". */
function accountSummary(a: Account): string {
  const stage = accountRollupStage(a);
  const bits = [a.name];
  if (stage) bits.push(`— ${stage}`);
  const meta = [
    a.ownerName ? `owner ${a.ownerName}` : null,
    a.pocName ? `POC ${a.pocName}` : null,
    a.workflows.length ? plural(a.workflows.length, 'workflow') : null,
  ].filter(Boolean);
  let out = bits.join(' ');
  if (meta.length) out += `. ${meta.join(', ')}.`;
  return out;
}

/** Pull the entity name out of "tell me about X" / "who is X" phrasings. */
function extractEntityQuery(text: string): string | undefined {
  const m =
    /\b(?:tell\s+me\s+about|who\s+is|who'?s|what'?s\s+the\s+status\s+of|status\s+of|info\s+on|details?\s+on|look\s+up|find|show\s+me)\s+(.+)/i.exec(
      text,
    );
  if (!m) return undefined;
  return m[1].replace(/[?.!]+$/, '').replace(/^(?:the\s+|our\s+|my\s+)/i, '').trim();
}

/**
 * Answer a read-only question from the live store. Returns the answer text,
 * or null when the message isn't a question this can field (so the caller
 * falls back to HELP). Proposes no writes.
 */
function answerDataQuestion(raw: string, ctx: AgentContext): string | null {
  const text = stripPreamble(raw);
  const q = norm(text);
  const people = ctx.people;
  const accounts = ctx.accounts;
  const tickets = ctx.tickets;
  const tasks = ctx.tasks ?? [];
  const prospects = people.filter((p) => p.category === 'prospect');
  const customers = people.filter((p) => p.category === 'customer');
  const openProspects = prospects.filter(
    (p) => p.stage !== 'closed-won' && p.stage !== 'closed-lost',
  );

  // --- Specific ticket status: "what's the status of TIQ-101?" -----------
  const tiq = /\btiq-?(\d+)\b/i.exec(q);
  if (tiq) {
    const id = `TIQ-${tiq[1].padStart(3, '0')}`;
    const t =
      tickets.find((x) => norm(x.id) === norm(id)) ||
      tickets.find((x) => norm(x.id).endsWith(tiq[1]));
    if (t) {
      return `${t.id} “${t.title}” — ${t.status} (${t.priority}), owner ${
        t.owner ?? 'unclaimed'
      }.`;
    }
    return `I don't see a ticket ${id} in the system.`;
  }

  const isQuestion =
    /\?/.test(raw) ||
    /\b(how\s+many|how\s+much|number\s+of|count\s+of|list|show|who|what|which|do\s+we|are\s+there|tell\s+me)\b/.test(
      q,
    );

  // --- Counts: "how many prospects / customers / tickets do we have?" -----
  if (/\b(how\s+many|number\s+of|count\s+of)\b/.test(q)) {
    if (/\bprospect/.test(q)) {
      const breakdown = tally(prospects, (p) => p.stage);
      return `${plural(prospects.length, 'prospect')}${breakdown ? ` (${breakdown})` : ''}.`;
    }
    if (/\bcustomer|client/.test(q)) {
      const breakdown = tally(customers, (p) => p.status);
      return `${plural(customers.length, 'customer')}${breakdown ? ` (${breakdown})` : ''}.`;
    }
    if (/\baccount/.test(q)) return `${plural(accounts.length, 'account')}.`;
    if (/\bticket/.test(q)) {
      const open = tickets.filter((t) => t.status !== 'done' && t.status !== 'cancelled');
      return `${plural(tickets.length, 'ticket')} — ${open.length} open / ${
        tickets.length - open.length
      } closed.`;
    }
    if (/\btask/.test(q)) {
      const open = tasks.filter((t) => t.status !== 'done');
      return `${plural(tasks.length, 'task')} — ${open.length} still open.`;
    }
    if (/\bdeal/.test(q)) return `${plural(openProspects.length, 'open deal')} in the pipeline.`;
    if (/\b(contact|people|person|staff|team)\b/.test(q)) {
      const breakdown = tally(people, (p) => p.category);
      return `${plural(people.length, 'contact')}${breakdown ? ` (${breakdown})` : ''}.`;
    }
  }

  // --- Pipeline / revenue value ------------------------------------------
  if (/\b(pipeline|book\s+of\s+business|total\s+(?:deal\s+)?value|how\s+much.*(?:pipeline|deal|worth)|arr|revenue|recurring)\b/.test(q)) {
    const pipe = openProspects.reduce((s, p) => s + (p.estimatedValue ?? 0), 0);
    const arr = customers
      .filter((c) => c.status !== 'churned')
      .reduce((s, c) => s + (c.accountValue ?? 0), 0);
    return `Open pipeline: ${usd(pipe)} across ${plural(openProspects.length, 'deal')}. Active ARR: ${usd(arr)} across ${plural(customers.filter((c) => c.status !== 'churned').length, 'customer')}.`;
  }

  // --- Superlatives: biggest deal / top prospect / top customer ----------
  if (/\b(biggest|largest|top|highest|best|most\s+valuable)\b/.test(q)) {
    if (/\bcustomer|client|account\b/.test(q)) {
      const top = [...customers].sort((a, b) => (b.accountValue ?? 0) - (a.accountValue ?? 0))[0];
      if (top) return `Top customer: ${top.name}${top.company ? ` (${top.company})` : ''} at ${usd(top.accountValue)}/yr.`;
    }
    const top = [...openProspects].sort(
      (a, b) => (b.estimatedValue ?? 0) - (a.estimatedValue ?? 0) || (b.score ?? 0) - (a.score ?? 0),
    )[0];
    if (top) {
      return `Biggest open deal: ${top.name}${top.company ? ` @ ${top.company}` : ''} — ${usd(
        top.estimatedValue,
      )}${top.stage ? `, stage ${top.stage}` : ''}.`;
    }
    return 'No open deals to rank yet.';
  }

  // --- Lists: "list my customers", "show open tickets" -------------------
  if (/\b(list|show|who\s+are|give\s+me|what\s+are)\b/.test(q)) {
    if (/\bprospect|deal|pipeline/.test(q)) {
      if (!openProspects.length) return 'No open prospects right now.';
      const lines = [...openProspects]
        .sort((a, b) => (b.estimatedValue ?? 0) - (a.estimatedValue ?? 0))
        .map((p) => `• ${p.name}${p.company ? ` @ ${p.company}` : ''} — ${p.stage ?? 'new'}${p.estimatedValue ? `, ${usd(p.estimatedValue)}` : ''}`);
      return `Open prospects (${openProspects.length}):\n${capped(lines)}`;
    }
    if (/\bcustomer|client/.test(q)) {
      if (!customers.length) return 'No customers yet.';
      const lines = [...customers]
        .sort((a, b) => (b.accountValue ?? 0) - (a.accountValue ?? 0))
        .map((c) => `• ${c.name}${c.company ? ` (${c.company})` : ''} — ${c.status ?? 'active'}${c.accountValue ? `, ${usd(c.accountValue)}/yr` : ''}`);
      return `Customers (${customers.length}):\n${capped(lines)}`;
    }
    if (/\baccount/.test(q)) {
      if (!accounts.length) return 'No accounts yet.';
      const lines = accounts.map((a) => `• ${a.name}${accountRollupStage(a) ? ` — ${accountRollupStage(a)}` : ''}`);
      return `Accounts (${accounts.length}):\n${capped(lines)}`;
    }
    if (/\bticket/.test(q)) {
      const open = tickets.filter((t) => t.status !== 'done' && t.status !== 'cancelled');
      if (!open.length) return 'No open tickets — the queue is clear. 🎉';
      const lines = open.map((t) => `• ${t.id} ${t.title} — ${t.status} (${t.priority})`);
      return `Open tickets (${open.length}):\n${capped(lines)}`;
    }
    if (/\btask/.test(q)) {
      const open = tasks.filter((t) => t.status !== 'done');
      if (!open.length) return 'No open tasks.';
      const lines = open.map((t) => `• ${t.title} — ${t.status}${t.dueDate ? `, due ${t.dueDate}` : ''}`);
      return `Open tasks (${open.length}):\n${capped(lines)}`;
    }
  }

  // --- Entity lookup: "tell me about Acme" / "who is Jane?" ---------------
  const entity = extractEntityQuery(text);
  if (entity && entity.length >= 2) {
    const acct = findAccount(accounts, entity);
    const person = findPerson(people, entity);
    // Prefer whichever matched the name more exactly.
    if (acct && (!person || norm(acct.name) === norm(entity))) return accountSummary(acct);
    if (person) return personSummary(person);
    if (acct) return accountSummary(acct);
    return `I don't have anyone or any account matching “${entity}”. Add them with “Add prospect ${entity}” and I’ll propose the card.`;
  }

  // Generic question we couldn't field → let the caller fall back to HELP,
  // but only swallow it if it actually read like a question.
  if (isQuestion && /\b(crm|pipeline|deals?|prospects?|customers?|accounts?|tickets?|tasks?)\b/.test(q)) {
    return `I can tell you counts, your pipeline value, your biggest deal, or details on a specific contact/account. Try “how many prospects?”, “what's my biggest deal?”, or “tell me about <name>”.`;
  }

  return null;
}

// --- Roster paste-import (agentic onboarding) -------------------------------
// Detects a multi-line pasted contact list — "Jane Doe, Acme, jane@acme.com,
// $48k", one per line, optional CSV header, optional "customers:" directive —
// and proposes one create_person per line. This powers first-run onboarding:
// paste your book of business into the chat, confirm the cards, and the CRM
// is populated without ever touching a form. Conservative by design: it only
// fires on multi-line input where most lines parse as contacts, so prose
// pastes fall through to the normal parsers.

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;
const ROSTER_TITLE_RE =
  /\b(vp|vice president|head of|head|director|chief|cto|ceo|cfo|coo|founder|co-?founder|manager|lead|principal|engineer|officer|president|partner|owner)\b/i;
const ROSTER_MONEY_RE =
  /(?:\$\s*\d[\d,]*(?:\.\d+)?\s*[kKmM]?\b|\b\d+(?:\.\d+)?\s*[kK]\b|\bworth\s+\$?\d[\d,]*(?:\.\d+)?\s*[kKmM]?\b)/;

interface RosterEntry {
  name: string;
  email?: string;
  company?: string;
  title?: string;
  value?: number;
}

/** Parse one pasted line into a contact, or null if it doesn't read as one. */
function parseRosterLine(raw: string): RosterEntry | null {
  // Strip list bullets / numbering ("- ", "* ", "3. ", "3) ").
  let line = raw.replace(/^\s*(?:[-*•]|\d+[.)])\s*/, '').trim();
  if (!line) return null;

  const email = EMAIL_RE.exec(line)?.[0];
  if (email) line = line.replace(email, '|');

  let value: number | undefined;
  const money = ROSTER_MONEY_RE.exec(line);
  if (money) {
    value = parseMoney(money[0]);
    line = line.replace(money[0], '|');
  }

  // Delimited fields first ("Name, Company, Title"); fall back to the
  // "Name at Company" form when the line has no usable delimiters.
  const fields = line
    .split(/[,\t;|]+|\s+[—–]\s+|\s+-\s+/)
    .map((f) => f.trim())
    .filter(Boolean);

  let name = '';
  let company: string | undefined;
  let title: string | undefined;

  if (fields.length >= 2) {
    name = fields[0];
    if (ROSTER_TITLE_RE.test(fields[1]) && fields[2]) {
      title = fields[1];
      company = fields[2];
    } else {
      company = fields[1];
      if (fields[2] && ROSTER_TITLE_RE.test(fields[2])) title = fields[2];
    }
  } else {
    const flat = fields[0] ?? '';
    const at = /^(.*?)\s+(?:at|@)\s+(.+)$/.exec(flat);
    if (at) {
      name = at[1].trim();
      company = at[2].trim();
    } else if (email && flat) {
      name = flat;
    } else {
      return null;
    }
  }

  // The "name at Company" form can also hide inside a delimited first field.
  const nestedAt = /^(.*?)\s+(?:at|@)\s+(.+)$/.exec(name);
  if (nestedAt) {
    name = nestedAt[1].trim();
    if (!company) company = nestedAt[2].trim();
  }

  // Sanity: a name has letters, isn't an email fragment, isn't absurdly long.
  if (!/[A-Za-z]{2}/.test(name) || name.length > 60 || EMAIL_RE.test(name)) return null;

  return { name, email, company, title, value };
}

/**
 * Try to read a whole message as a pasted roster. Returns a full reply
 * (note + one create_person per parsed line) or null to fall through.
 */
function parseRoster(text: string, ctx: AgentContext): AgentReply | null {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length < 2) return null;

  // Optional directive line: "customers:", "import my prospects", "leads —".
  let category: 'customer' | 'prospect' = 'prospect';
  let start = 0;
  const head = norm(lines[0]);
  const directive =
    /^(?:import|add|these are|here are)?\s*(?:my|our)?\s*(customers?|clients?|prospects?|leads?)\b/.exec(head);
  if (directive && !EMAIL_RE.test(lines[0]) && lines[0].length < 60) {
    if (/customer|client/.test(directive[1])) category = 'customer';
    start = 1;
  }

  const body = lines.slice(start);
  // Skip a CSV-style header row ("Name, Company, Email, Deal").
  if (body.length > 1 && /^(?:full\s*)?name\b/.test(norm(body[0])) && /email|company|deal|value|title/.test(norm(body[0]))) {
    body.shift();
  }
  if (body.length < 2) return null;

  const entries: RosterEntry[] = [];
  let unparsed = 0;
  for (const line of body) {
    const entry = parseRosterLine(line);
    if (entry) entries.push(entry);
    else unparsed++;
  }
  // Most lines must parse, or this is prose — not a roster.
  if (entries.length < 2 || entries.length < body.length * 0.6) return null;

  // Dedupe against the live store (by email, else name+company).
  const fresh: RosterEntry[] = [];
  const dupes: string[] = [];
  for (const entry of entries) {
    const exists = ctx.people.some(
      (p) =>
        (entry.email && p.email && norm(p.email) === norm(entry.email)) ||
        (norm(p.name) === norm(entry.name) &&
          (!entry.company || !p.company || norm(p.company) === norm(entry.company))),
    );
    if (exists) dupes.push(entry.name);
    else fresh.push(entry);
  }

  if (fresh.length === 0) {
    return {
      text: `Everyone on that list is already in the CRM (${dupes.join(', ')}) — nothing to import.`,
      actions: [],
    };
  }

  const actions: AgentAction[] = fresh.map((entry) => ({
    kind: 'create_person' as const,
    category,
    name: entry.name,
    email: entry.email,
    company: entry.company,
    title: entry.title,
    value: entry.value,
    ...(category === 'prospect'
      ? { stage: 'new' as const }
      : { status: 'active' as const }),
  }));

  const bits: string[] = [
    `That reads as a roster — ${fresh.length} ${category === 'customer' ? 'customer' : 'prospect'}${fresh.length === 1 ? '' : 's'} ready to import. Confirm each card to write it, or dismiss any I misread.`,
  ];
  if (dupes.length) bits.push(`Skipped ${dupes.length} already in the CRM: ${dupes.join(', ')}.`);
  if (unparsed > 0) bits.push(`${unparsed} line${unparsed === 1 ? '' : 's'} didn’t read as a contact — paste as “Name, Company, email, $value” and I’ll pick ${unparsed === 1 ? 'it' : 'them'} up.`);

  return { text: bits.join('\n'), actions };
}

export const localBackend: AgentBackend = {
  name: 'local',
  async interpret(message: string, ctx: AgentContext): Promise<AgentReply> {
    const text = message.trim();
    const lower = norm(text);

    // Empty / greeting / help → capabilities.
    if (!text || /^\s*(hi|hey|hello|yo|help|\?)\s*$/i.test(text)) {
      return { text: HELP, actions: [] };
    }

    // Question intent ("what should I do today?", "anything overdue?") →
    // answer from the live scanner, with one-click proposals for the top
    // findings so the reply carries buttons, not prose offers.
    if (isWorkQuestion(text)) {
      return answerWorkQuestion(ctx);
    }

    // Multi-line input → try reading it as a pasted contact roster first
    // (agentic onboarding: paste your book of business, confirm the cards).
    if (text.includes('\n')) {
      const roster = parseRoster(text, ctx);
      if (roster) return roster;
    }

    // Parse each clause, then merge — deduping identical actions so a
    // phrase that matches two patterns doesn't propose the same write twice.
    const actions: AgentAction[] = [];
    const notes: string[] = [];
    for (const clause of splitClauses(text)) {
      const result = parseClause(clause, ctx);
      for (const action of result.actions) {
        const dupe = actions.some(
          (existing) => JSON.stringify(existing) === JSON.stringify(action),
        );
        if (!dupe) actions.push(action);
      }
      for (const note of result.notes) {
        if (!notes.includes(note)) notes.push(note);
      }
    }

    let textReply: string;
    if (actions.length > 0) {
      const lead =
        actions.length === 1
          ? 'Here’s what I’ll do — confirm to run it:'
          : `Here are ${actions.length} actions — confirm each to run it:`;
      textReply = notes.length ? `${lead}\n\n${notes.join('\n')}` : lead;
    } else if (notes.length) {
      textReply = notes.join('\n');
    } else if (/\b(help|what can you do|commands?|how do)\b/.test(lower)) {
      textReply = HELP;
    } else {
      // No write parsed — try to answer it as a read-only question about the
      // live store before giving up. This is the safe place for the data
      // answerer: every command parser already ran, so it can't shadow a write.
      const answer = answerDataQuestion(text, ctx);
      if (answer) {
        textReply = answer;
        // If the question resolved to a known entity, attach one-click
        // proposals (create follow-up task / log a note / …) so the answer
        // can act — never a prose "want me to…?" with nothing to click.
        // Still confirm-each-write: these are pending cards, not writes.
        const followUps = entityFollowUpActions(text, ctx);
        if (followUps.length) {
          actions.push(...followUps);
          textReply += '\n\nOne-click actions below — confirm any to run it.';
        }
      } else {
        textReply = `I didn’t spot an action in that. ${HELP}`;
      }
    }

    return { text: textReply, actions };
  },
};
